import type { AppStartListening, RootState } from '@castlery/shared-redux-store';
import {
  tokenRefreshedEvent,
  tokenReceived,
  Tokens,
  getCurrentAdmin,
  EnterAppEvent,
  getUserByUid,
  setCustomer,
  customerUpdatedEvent,
  loggedOutEvent,
  selectedCustomerId,
  initDefaultZipcode,
  InitWishListEvent,
  getWishList,
  getTheLookWishList,
  setWishList,
  setTheLookWishList,
  TheLookWishListEvent,
  getCurrentUser,
  noticeCityInfoUpdated,
  setUser,
  getCustomerAddress,
  customerApiErrorEvent,
  customerAddressUpdatedEvent,
  userSliceUpdatedEvent,
  setCustomerAddress,
  userAddressGetEvent,
  customerReviewUpdatedEvent,
  getCustomerReviews,
  setCustomerReviews,
  customerReviewGetEvent,
  userNeedUpdatedEvent,
  userSubscriptionsGetEvent,
  setUserSubscriptions,
  getUserSubscriptions,
} from '@castlery/modules-user-domain';
import { PayloadAction, Unsubscribe, isAnyOf } from '@reduxjs/toolkit';
import { ExtraArgument } from '@castlery/shared-redux-extra';
import { EcEnv, enableZipCode } from '@castlery/config';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { resetAutoOnlineCartSymbol } from '@castlery/modules-composite-services';
import {
  EVENT_CUSTOMER_IDENTIFY,
  EVENT_PAGE_VIEW_AFTER_AUTH_BY_MODAL,
  trackUserIdentify,
} from '@castlery/modules-tracking-services';
import { User } from '@castlery/types';
/**
 * Subscribes counter listeners and returns a `teardown` function.
 * @example
 * ```ts
 * useEffect(() => {
 *   const unsubscribe = setupCounterListeners();
 *   return unsubscribe;
 * }, []);
 * ```
 */
export function setupUserListeners(startListening: AppStartListening, { modal }: { modal: any }): Unsubscribe {
  const subscriptions = [
    /* -------------------------------------------------------------------------- */
    /*                                    共享                                     */
    /* -------------------------------------------------------------------------- */
    // startListening({
    //   matcher: isAnyOf(InitWishListEvent, TheLookWishListEvent),
    //   effect: async (action, { dispatch }) => {
    //     const result: any = await Promise.allSettled([
    //       dispatch(getWishList.initiate(undefined, { forceRefetch: true })),
    //       dispatch(getTheLookWishList.initiate(undefined, { forceRefetch: true })),
    //     ]);

    //     await Promise.allSettled([
    //       dispatch(setWishList(result[0].value?.data || [])),
    //       dispatch(setTheLookWishList(result[1].value?.data || [])),
    //     ]);
    //   },
    // }),
    // the look wishlist
    // startListening({
    //   matcher: TheLookWishListEvent,
    //   effect: async (action, { dispatch }) => {
    //     console.log('--------TheLook的监听器----------');

    //     const result: any = await Promise.allSettled([
    //       dispatch(getWishList.initiate()),
    //       dispatch(getTheLookWishList.initiate()),
    //     ]);
    //     await Promise.allSettled([
    //       dispatch(setWishList(result[0].value?.data || [])),
    //       dispatch(setTheLookWishList(result[1].value?.data || [])),
    //     ]);
    //   },
    // }),
    startListening({
      // 登出的时候
      actionCreator: loggedOutEvent,
      effect: () => {
        location.reload();
      },
    }),
    startListening({
      // 登录 或者 刷新token 的时候
      matcher: tokenRefreshedEvent,
      // matcher: isAnyOf(loggedInEvent, tokenRefreshedEvent),
      effect: async (action: PayloadAction<Tokens>, { dispatch }) => {
        const { payload } = action;
        await dispatch(tokenReceived(payload));
        await dispatch(getCurrentAdmin.initiate());
        //事件上报
      },
    }),

    /* -------------------------------------------------------------------------- */
    /*                                  POS 独享                                   */
    /* -------------------------------------------------------------------------- */
    ...((EcEnv.NEXT_PUBLIC_CHANNEL === 'POS' && [
      startListening({
        actionCreator: EnterAppEvent,
        effect: async (action, { dispatch }) => {
          await dispatch(getCurrentAdmin.initiate());
        },
      }),
      startListening({
        actionCreator: EnterAppEvent,
        effect: async ({ payload }) => {
          // 更新page title
          let title = '';
          switch (payload) {
            case 'checkout':
              title = 'Checkout';
              break;
            case 'SALES HISTORY':
              title = 'Sales History';
              break;
            default:
              title = 'Castlery POS';
              break;
          }
          document.title = title;
        },
      }),
      /**
       * 更新 customer 到 store
       */
      startListening({
        actionCreator: EnterAppEvent,
        effect: async (action, { dispatch, extra, getState }) => {
          const rootState = getState() as RootState;
          const customerIdFromRedux = selectedCustomerId(rootState);
          const { persistenceHandles } = extra as ExtraArgument;
          const customerIdFromLocalStorage = Number(await persistenceHandles.customerId.getItem());
          if (enableZipCode) {
            const res = await dispatch(initDefaultZipcode());
            dispatch(noticeCityInfoUpdated(res.payload));
          }
          if (customerIdFromRedux && customerIdFromLocalStorage && customerIdFromLocalStorage === customerIdFromRedux) {
            return;
          }

          if (customerIdFromLocalStorage) {
            // 获取用户信息
            const { data, error } = await dispatch(getUserByUid.initiate(customerIdFromLocalStorage));
            if (error) {
              return;
            }
            if (data) {
              dispatch(setCustomer(data));
            }
          } else {
            dispatch(resetAutoOnlineCartSymbol());
          }
        },
      }),
      // customer 变化的时候 更新到 localStorage
      startListening({
        actionCreator: customerUpdatedEvent,
        effect: (action, { extra }) => {
          const { persistenceHandles } = extra as ExtraArgument;
          const customer = action.payload;
          persistenceHandles.customerId.setItem(customer?.id + '');
          persistenceHandles.autoAppliedCoupon.removeItem();
        },
      }),
    ]) ||
      []),
    /* -------------------------------------------------------------------------- */
    /*                                  WEB 独享                                   */
    /* -------------------------------------------------------------------------- */
    ...((EcEnv.NEXT_PUBLIC_CHANNEL === 'WEB' && [
      startListening({
        actionCreator: userSliceUpdatedEvent,
        effect: (action, { dispatch }) => {
          const customer = action.payload;
          customer?.email && dispatch(trackUserIdentify());
        },
      }),
      startListening({
        actionCreator: EnterAppEvent,
        effect: async (action, { dispatch, extra }) => {
          const { persistenceHandles } = extra as ExtraArgument;
          const webAccessToken = persistenceHandles.webAccessToken.getItem();
          if (webAccessToken) {
            const { data, error } = await dispatch(getCurrentUser.initiate(undefined, { forceRefetch: true }));
            if (error) {
              return;
            }
            if (data) {
              dispatch(setUser(data));
              dispatch(EVENT_CUSTOMER_IDENTIFY({ isSignup: action.payload === 'Signup' }));
              if (action.payload === 'Signup' || action.payload === 'Signin') {
                dispatch(EVENT_PAGE_VIEW_AFTER_AUTH_BY_MODAL({ customer: data as User }));
              }
            }
          }
        },
      }),
      startListening({
        matcher: isAnyOf(InitWishListEvent, TheLookWishListEvent, EnterAppEvent),
        effect: async (action, { dispatch, extra }) => {
          const { persistenceHandles } = extra as ExtraArgument;
          const webAccessToken = persistenceHandles.webAccessToken.getItem();
          if (action.type === EnterAppEvent.type && action['payload'] !== 'Signin' && action['payload'] !== 'Signup') {
            return;
          }
          const result: any = await Promise.allSettled([
            dispatch(getWishList.initiate(undefined, { forceRefetch: true })),
            dispatch(getTheLookWishList.initiate(undefined, { forceRefetch: true })),
          ]);

          await Promise.allSettled([
            dispatch(setWishList(result[0].value?.data || [])),
            dispatch(setTheLookWishList(result[1].value?.data || [])),
          ]);
          if (webAccessToken) {
            persistenceHandles.guestToken.removeItem();
            persistenceHandles.wishlistToken.removeItem();
            persistenceHandles.wishItemGuestToken.removeItem();
          }
        },
      }),
      startListening({
        matcher: userNeedUpdatedEvent,
        effect: async (action, { dispatch }) => {
          dispatch(getUserSubscriptions.initiate(undefined, { forceRefetch: true }));
          const { data, error } = await dispatch(getCurrentUser.initiate(undefined, { forceRefetch: true }));
          if (error) {
            return;
          }
          if (data) {
            dispatch(setUser(data));
          }
        },
      }),
      startListening({
        matcher: userSubscriptionsGetEvent,
        effect: async (action, { dispatch }) => {
          const subscriptions = action.payload;
          if (subscriptions) {
            dispatch(setUserSubscriptions(subscriptions));
          }
        },
      }),
      startListening({
        matcher: customerApiErrorEvent,
        effect: (action) => {
          const extractErrorMessage = (payload: any): string => {
            try {
              // 安全地提取错误信息，避免 JSON 序列化问题
              if (!payload) return 'Something went wrong';

              // 直接访问属性，不使用 JSON 序列化
              const data = payload.data;
              if (data?.errors?.length > 0) {
                return data.errors[0]?.detail || data.errors[0]?.title || 'Something went wrong';
              }

              // 检查其他可能的错误格式
              if (data?.message) return data.message;
              if (data?.error) return data.error;
              if (payload.message) return payload.message;
              if (payload.error) return payload.error;

              return 'Something went wrong';
            } catch (err) {
              // 如果提取过程中出现任何错误，返回默认消息
              console.warn('Error extracting error message:', err);
              return 'Something went wrong';
            }
          };

          const error = extractErrorMessage(action.payload);
          modal.warning({
            title: 'Oops!',
            desc: error,
            showCancelBtn: false,
            confirmText: 'Got it',
          });
        },
      }),
      startListening({
        matcher: customerAddressUpdatedEvent,
        effect: async (action, { dispatch }) => {
          dispatch(getCustomerAddress.initiate(undefined, { forceRefetch: true }));
        },
      }),
      startListening({
        matcher: userAddressGetEvent,
        effect: async (action, { dispatch }) => {
          const address = action.payload;
          if (address) {
            dispatch(setCustomerAddress(address));
          }
        },
      }),
      startListening({
        matcher: customerReviewUpdatedEvent,
        effect: async (action, { dispatch }) => {
          dispatch(getCustomerReviews.initiate(undefined, { forceRefetch: true }));
        },
      }),
      startListening({
        matcher: customerReviewGetEvent,
        effect: (action, { dispatch }) => {
          const reviews = action.payload;
          if (reviews) {
            dispatch(setCustomerReviews(reviews));
          }
        },
      }),
    ]) ||
      []),
  ];

  return () => {
    subscriptions.forEach((unsubscribe) => unsubscribe());
  };
}
